import React, { useEffect, useState } from 'react'

import { Box, useTheme, useMediaQuery } from '@mui/material'
import { Button, Typography, Modal } from '@evocolabs/orkaui'

import styles from '/src/components/scss/Account/AccountInfo.module.scss'
import { statesChoice } from 'src/components/Checkout/Delivery/State.js'
import { useSelector } from 'react-redux'
import {
  ItemDisplay,
  NameField,
  DropdownField,
  PasswordField,
  HearingConditionDropdownField
} from './AccountInfoComponents'
import { selector } from '@/src/redux/store'

/* Define constants */
import { useBackend } from '@/src/providers/BackendProvider'
const years = []
const currentyear = new Date().getFullYear()
for (let i = 1920; i <= currentyear; i++) years.push(i.toString())
const genderChoice = ['Male', 'Female', 'Other']
const hearingConditionChoice = [
  'Mild',
  'Moderate',
  'Moderately Severe',
  'Severe',
  `I'm not sure`
]

const AccountInfo = ({ info }) => {
  const theme = useTheme()
  const usePC = useMediaQuery(theme.breakpoints.up('md'))
  const { userSignOut, getLatestHearingCondition } = useBackend()
  const handleSignOut = async () => {
    await userSignOut()
  }
  // set user display name, considering null for both first and last name
  if (info.firstName && info.lastName) {
    info.name = info.firstName + ' ' + info.lastName
  } else {
    info.firstName = info.firstName || ''
    info.lastName = info.lastName || ''
    info.name = info.firstName + info.lastName
  }

  const [activeFormTitle, setActiveFormTitle] = useState(null)
  const [addressModelOpen, setAddressModelOpen] = useState(false)
  const [hasKYCandHearingCondition, setHasKYCAndHearingCondition] =
    useState(false)
  const loggedUser = useSelector(selector.selectUserInfo).uuid

  useEffect(() => {
    getLatestHearingCondition(loggedUser).then((res) => {
      if (res.hearingCondition !== null) {
        setHasKYCAndHearingCondition(true)
      } else {
        setHasKYCAndHearingCondition(false)
      }
    })
  }, [])
  const filteredAddress = []
  const filteredAddressStr = []
  info.address?.forEach((address) => {
    const addressStr = `${address.address1}, ${address.address2}, ${address.city}, ${address.state}, ${address.country}, ${address.zip_code}, ${address.name}, ${address.phone_number}`
    if (
      !filteredAddressStr.includes(addressStr) &&
      addressStr.replace(/undefined| |,/g, '') !== ''
    ) {
      filteredAddress.push(address)
      filteredAddressStr.push(addressStr)
    }
  })

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Box
        sx={{
          padding: '16px 40px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          display: { xs: 'none', md: 'flex' }
        }}
      >
        <Typography variant="T1">Account Details</Typography>
      </Box>
      <Box className={styles.card}>
        <Typography variant="T1" width="100%">
          Account Info
        </Typography>
        <NameField
          title="Name"
          content={info.name}
          firstName={info.firstName}
          lastName={info.lastName}
          activeFormTitle={activeFormTitle}
          setActiveFormTitle={setActiveFormTitle}
        />
        <ItemDisplay
          title="Email"
          content={info.email}
          activeFormTitle={activeFormTitle}
          setActiveFormTitle={setActiveFormTitle}
          disableEditing
        />
        <DropdownField
          title="Year of Birth"
          fieldName="birth_year"
          content={info.year}
          activeFormTitle={activeFormTitle}
          setActiveFormTitle={setActiveFormTitle}
          choices={years}
          id="birth-year"
        />
        <DropdownField
          title="Gender"
          fieldName="gender"
          content={info.gender}
          activeFormTitle={activeFormTitle}
          setActiveFormTitle={setActiveFormTitle}
          choices={genderChoice}
          id="gender"
        />
        <DropdownField
          title="State"
          fieldName="state"
          content={info.state}
          activeFormTitle={activeFormTitle}
          setActiveFormTitle={setActiveFormTitle}
          choices={statesChoice}
          id="state"
        />
        {hasKYCandHearingCondition && (
          <HearingConditionDropdownField
            title="Hearing Condition"
            fieldName="hearing_condition"
            activeFormTitle={activeFormTitle}
            setActiveFormTitle={setActiveFormTitle}
            choices={hearingConditionChoice}
          />
        )}

        <ItemDisplay
          title="Delivery Address"
          content={
            <Box>
              {info.address?.length > 0 ? (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={'flex-start'}
                >
                  <Box display="flex" flexDirection="column" gap={'2px'}>
                    <div>
                      <Typography variant="Body3">
                        {filteredAddress[0].name}
                      </Typography>{' '}
                      <Typography variant="Body3">
                        {filteredAddress[0].phone_number}
                      </Typography>
                    </div>
                    <Typography variant="body3">
                      {[
                        filteredAddress[0].address1,
                        filteredAddress[0].address2,
                        filteredAddress[0].city,
                        filteredAddress[0].state,
                        filteredAddress[0].country,
                        filteredAddress[0].zip_code
                      ]
                        .filter((d) => d)
                        .join(', ')}
                    </Typography>
                  </Box>
                  <Button
                    variant="textprimary"
                    onClick={() => setAddressModelOpen(true)}
                    flex={1}
                    id="account-details-page-address-section-more-button"
                  >
                    More
                  </Button>
                  <Modal
                    open={addressModelOpen}
                    title="Delivery Address"
                    disableButtons
                    onCancel={() => setAddressModelOpen(false)}
                    closable
                    content={
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={'0px'}
                        sx={{
                          ':last-child': { borderBottom: 'unset' }
                        }}
                      >
                        {filteredAddress.map((item, idx) => (
                          <Box
                            display="flex"
                            flexDirection="column"
                            gap={'2px'}
                            padding="4px 0"
                            borderBottom={'1px solid #E5E4E3'}
                            key={idx}
                          >
                            <Box
                              display={'flex'}
                              gap={1}
                              alignItems={'flex-end'}
                            >
                              <Typography variant="Body2">
                                {item.name}
                              </Typography>
                              <Typography variant="body3">
                                {item.phone_number}
                              </Typography>
                            </Box>
                            <Typography variant="body3">
                              {[
                                item.address1,
                                item.address2,
                                item.city,
                                item.state,
                                item.country,
                                item.zip_code
                              ]
                                .filter((d) => d)
                                .join(', ')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    }
                  />
                </Box>
              ) : (
                '/'
              )}
            </Box>
          }
          activeFormTitle={activeFormTitle}
          setActiveFormTitle={setActiveFormTitle}
          disableEditing
        />
      </Box>
      <Box className={styles.card}>
        <Typography variant="T1">Manage Password</Typography>
        <PasswordField
          title="Password"
          email={info.email}
          activeFormTitle={activeFormTitle}
          setActiveFormTitle={setActiveFormTitle}
        />
      </Box>
      {!usePC && (
        <Box
          className={styles.card}
          onClick={handleSignOut}
          sx={{ cursor: 'pointer' }}
        >
          <Typography variant="T2" textAlign="center">
            Log Out
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default AccountInfo
